import { type FC, useCallback } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  directContactFormAccessorSubjectRequiresLogin,
  useDirectContactFormPrefillCustomerData,
  useDirectContactFormSubjectLoginModal,
  useDirectContactFormSubmit,
} from '../../../direct-contact'
import { useHookFormFriendlyCaptchaFieldControls } from '../../../friendly-captcha'
import { LoginRegistrationUserLoginModal } from '../../../login-registration'
import { DirectContactFormOrderAuthenticationContext } from '../../context/DirectContactFormOrderAuthenticationContext'
import { directContactFormAccessorGetLoggedOutNotification } from '../../model/directContactFormAccessorGetLoggedOutNotification'

import { DirectContactFormContent } from './DirectContactFormContent'
import {
  DIRECT_CONTACT_FORM_DEFAULT_VALUES,
  type DirectContactFormValues,
} from './DirectContactFormModel'

export const DirectContactForm: FC<{ title: string }> = ({ title }) => {
  const intl = useIntl()
  const formMethods = useForm<DirectContactFormValues>({
    defaultValues: DIRECT_CONTACT_FORM_DEFAULT_VALUES,
    mode: 'onBlur',
  })
  const { control, register, resetField, setValue, watch } = formMethods
  const [friendlyCaptchaSolution, subject] = useWatch({
    control,
    name: ['fcSolution', 'subject'],
  })
  const { isCustomerDataComplete, isCustomerLoggedIn } =
    useDirectContactFormPrefillCustomerData(setValue)
  const {
    handleLoginModalClose,
    handleLoginSuccess,
    handleOrderRequestAuthenticationError,
    isLoggedIn,
    loginModalControls,
  } = useDirectContactFormSubjectLoginModal({
    intl,
    isCustomerLoggedIn,
    selectedSubject: subject,
  })
  const { loggedOutNotificationType } =
    directContactFormAccessorGetLoggedOutNotification({
      intl,
      isLoggedIn,
      subject,
    })
  const currentSubjectRequiresLogin =
    directContactFormAccessorSubjectRequiresLogin({
      intl,
      subject,
    })
  const handleAuthenticationError = useCallback((): void => {
    resetField('fcSolution')
    handleOrderRequestAuthenticationError()
  }, [handleOrderRequestAuthenticationError, resetField])

  const submit = useDirectContactFormSubmit({
    onAuthenticationError: handleAuthenticationError,
  })
  const { isMutating, submitStatus } = submit
  const { onCaptchaDone } =
    useHookFormFriendlyCaptchaFieldControls<DirectContactFormValues>({
      fieldName: 'fcSolution',
      register,
      setValue,
      watch,
    })
  const isSubmitDisabled =
    !friendlyCaptchaSolution ||
    isMutating ||
    (!isLoggedIn &&
      (Boolean(loggedOutNotificationType) || currentSubjectRequiresLogin))
  const shouldRenderCaptcha =
    submitStatus !== 'authenticationError' || !loginModalControls.isOpen

  return (
    <FormProvider {...formMethods}>
      <DirectContactFormOrderAuthenticationContext.Provider
        value={handleOrderRequestAuthenticationError}
      >
        <DirectContactFormContent
          isCustomerDataComplete={isCustomerDataComplete}
          isLoggedIn={isLoggedIn}
          isSubmitDisabled={isSubmitDisabled}
          loggedOutNotificationType={loggedOutNotificationType}
          onCaptchaDone={onCaptchaDone}
          shouldRenderCaptcha={shouldRenderCaptcha}
          submit={submit}
          title={title}
        />
      </DirectContactFormOrderAuthenticationContext.Provider>
      <LoginRegistrationUserLoginModal
        afterLoggedIn={handleLoginSuccess}
        onClose={handleLoginModalClose}
        open={loginModalControls.isOpen}
      />
    </FormProvider>
  )
}
